import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft, Download } from "lucide-react";
import api, { apiError } from "@/lib/api";

export default function ReceiptPage() {
  const { number } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) {
      setErr("Chybí přístupový token. Otevři doklad z e-mailu, který jsi obdržel/a.");
      return;
    }
    api
      .get(`/receipts/${number}`, { params: { token } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e)));
  }, [number, token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 receipt-page">
        <div className="stb-card-static p-6 max-w-md w-full" data-testid="receipt-error">
          <p className="stb-h text-xl text-white">Doklad nelze zobrazit</p>
          <p className="text-[#A3A3A3] text-sm mt-2">{err}</p>
          <a href="/" className="stb-btn stb-btn-secondary mt-4 inline-flex"><ArrowLeft size={14} /> Zpět</a>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-center stb-label">Načítám…</div>;

  const d = data;
  const s = d.supplier || {};
  const vatPayer = s.vat_payer === true && Number(d.vat_amount) > 0;
  const fmt = (n) => `${Number(n).toFixed(2).replace(".", ",")} Kč`;
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("cs-CZ", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="receipt-page min-h-screen bg-[#B0B5BA] py-6 px-4 print:bg-white print:py-0 print:px-0">
      <style>{`
        @media print {
          body { background: white !important; }
          .print-hide { display: none !important; }
          .receipt-paper { box-shadow: none !important; border: none !important; }
        }
        .receipt-paper {
          background: #ffffff;
          color: #0E0E0E;
          border: 2px solid #0E0E0E;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
        }
      `}</style>

      <div className="max-w-3xl mx-auto print-hide flex justify-between items-center mb-4">
        <a href="/" className="stb-btn stb-btn-secondary"><ArrowLeft size={14} /> Zpět</a>
        <button onClick={() => window.print()} className="stb-btn stb-btn-primary" data-testid="receipt-print">
          <Printer size={14} /> Vytisknout / uložit PDF
        </button>
      </div>

      <article className="receipt-paper max-w-3xl mx-auto p-8 print:p-12" data-testid="receipt-paper">
        <header className="flex items-start justify-between border-b-2 border-[#0E0E0E] pb-4">
          <div>
            <p className="stb-mono text-[10px] tracking-[0.3em] text-[#666]">STAVBOŠ.CZ</p>
            <h1 className="stb-h text-3xl text-[#0E0E0E] leading-none mt-1">
              {vatPayer ? <>ZJEDNODUŠENÝ<br />DAŇOVÝ DOKLAD</> : <>DOKLAD<br />O ZAPLACENÍ</>}
            </h1>
            <p className="stb-mono text-xs text-[#666] mt-2">
              {vatPayer
                ? "vystaveno dle § 30a zákona č. 235/2004 Sb. o DPH"
                : "dodavatel není plátce DPH"}
            </p>
          </div>
          <div className="text-right">
            <p className="stb-label text-[#666]">Číslo dokladu</p>
            <p className="stb-h text-2xl text-[#FF6B00]" data-testid="receipt-number">{d.number}</p>
            <p className="stb-mono text-xs text-[#666] mt-2">Datum vystavení: <strong>{fmtDate(d.issue_date)}</strong></p>
            <p className="stb-mono text-xs text-[#666]">DUZP: <strong>{fmtDate(d.taxable_date)}</strong></p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 mt-6 text-sm">
          <div>
            <p className="stb-label text-[#666]">Dodavatel</p>
            <p className="stb-h text-lg text-[#0E0E0E] mt-1">{s.company}</p>
            <p>{s.address}</p>
            <p className="mt-2"><strong>IČO:</strong> {s.ic}</p>
            {vatPayer && s.dic ? <p><strong>DIČ:</strong> {s.dic}</p> : <p>Neplátce DPH</p>}
            {(s.bank_account || s.iban) && (
              <p className="mt-2">
                <strong>Účet:</strong> {s.bank_account}
                {s.bank_name ? ` · ${s.bank_name}` : ""}
                {s.iban ? <><br /><strong>IBAN:</strong> {s.iban}</> : null}
              </p>
            )}
            <p className="mt-2 stb-mono text-xs text-[#666]">{s.email} · {s.site}</p>
          </div>
          <div>
            <p className="stb-label text-[#666]">Odběratel</p>
            <p className="text-[#0E0E0E] mt-1" data-testid="receipt-customer">
              {d.customer_name || "—"}
            </p>
            <p className="stb-mono">{d.customer_email}</p>
            <p className="stb-mono text-[10px] text-[#666] mt-2">
              {vatPayer
                ? "Doklad bez identifikace odběratele (zjednodušený daňový doklad pro plnění do 10 000 Kč včetně DPH)."
                : "Doklad o zaplacení. Dodavatel není plátce DPH."}
            </p>
          </div>
        </section>

        <section className="mt-8">
          <p className="stb-label text-[#666]">Předmět plnění</p>
          <table className="w-full mt-2 border-collapse">
            <thead>
              <tr className="border-b-2 border-[#0E0E0E] text-left">
                <th className="py-2 text-xs uppercase tracking-wider">Popis</th>
                {vatPayer && (
                  <>
                    <th className="py-2 text-xs uppercase tracking-wider text-right w-32">Bez DPH</th>
                    <th className="py-2 text-xs uppercase tracking-wider text-right w-24">DPH 21%</th>
                  </>
                )}
                <th className="py-2 text-xs uppercase tracking-wider text-right w-32">Celkem</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#0E0E0E]/20">
                <td className="py-3" data-testid="receipt-service">{d.service}</td>
                {vatPayer && (
                  <>
                    <td className="py-3 text-right stb-mono">{fmt(d.vat_base)}</td>
                    <td className="py-3 text-right stb-mono">{fmt(d.vat_amount)}</td>
                  </>
                )}
                <td className="py-3 text-right stb-mono font-bold">{fmt(d.amount_total)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="bg-[#FF6B00] text-[#0E0E0E]">
                <td colSpan={vatPayer ? 3 : 1} className="py-3 px-3 stb-h text-lg uppercase">K úhradě celkem</td>
                <td className="py-3 px-3 text-right stb-h text-2xl" data-testid="receipt-total">{fmt(d.amount_total)}</td>
              </tr>
            </tfoot>
          </table>
        </section>

        <section className="mt-6 grid grid-cols-2 gap-6 text-sm">
          <div>
            <p className="stb-label text-[#666]">Způsob platby</p>
            <p>{d.payment_method}</p>
            {d.session_id && <p className="stb-mono text-[10px] text-[#666] mt-1 break-all">Stripe session: {d.session_id}</p>}
          </div>
          <div>
            <p className="stb-label text-[#666]">Stav</p>
            <p className="stb-h text-lg text-[#22C55E]">UHRAZENO</p>
          </div>
        </section>

        <footer className="mt-8 pt-4 border-t-2 border-[#0E0E0E] text-xs text-[#666] stb-mono">
          <p>Tento doklad je vygenerován elektronicky a je platný bez razítka a podpisu.</p>
          <p className="mt-1">Pro běžnou fakturu s identifikací odběratele kontaktujte {s.email}.</p>
        </footer>
      </article>
    </div>
  );
}
